import {Injectable} from '@angular/core';
import {defer, EMPTY, merge, Observable, of, timer} from 'rxjs';
import {catchError, filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {STATE_NAMES} from 'app/common/state-names';
import {StartingPage} from 'app/common/constants';
import {progressRoles} from 'app/original-models/access.model';
import {ColorThemeFromServerEnum} from 'app/models/profile/color-theme.model';
import {AuthenticationService} from 'app/original-services/authentication.service';
import {CompanyGroupService} from 'app/original-services/company-group.service';
import {FirstStepService} from 'app/original-services/first-step.service';
import {GAService} from 'app/original-services/ga.service';
import {HttpCancelService} from 'app/original-services/httpCancel.service';
import {PermissionsService} from 'app/original-services/permissions.service';
import {RoutingService} from 'app/original-services/routing.service';
import {StorageService} from 'app/original-services/storage.service';
import {DefaultRouteService} from 'app/services/default-route.service';
import {ColorThemeService} from 'app/services/profile/color-theme.service';

/**
 * Opens the app after a session has been stored.
 *
 * Sign-in and activation both subscribe to this, and only after a real login. A visit to the
 * login page must not, even when a token is still in storage: a 401 can land there with a stale
 * token, and loading the account from that would bounce the user again.
 *
 * The observable holds no timers of its own. Unsubscribing cancels the company wait and the
 * route delay.
 */
@Injectable({providedIn: 'root'})
export class PostLoginNavigationService {
    constructor(
        private authService: AuthenticationService,
        private companyGroupService: CompanyGroupService,
        private routingService: RoutingService,
        private storage: StorageService,
        private gaService: GAService,
        private firstStepService: FirstStepService,
        private httpCancelService: HttpCancelService,
        private permissionsService: PermissionsService,
        private colorThemeService: ColorThemeService,
        private defaultRouteService: DefaultRouteService
    ) {
    }

    enterApp(redirectUrl: string | null): Observable<void> {
        return defer(() => {
            this.storage.mfaToken = null;
            return merge(this.subscriptionFlags(), this.openCompanies(redirectUrl));
        }).pipe(
            takeUntil(this.httpCancelService.onCancelPendingRequests()),
            map(() => undefined)
        );
    }

    private subscriptionFlags(): Observable<void> {
        if (this.storage.model !== 'tenant') {
            return EMPTY;
        }
        return this.authService.getSubscription().pipe(
            take(1),
            tap(subscription => {
                this.storage.setStorageItem('tenantCancelledSub', subscription.status === 'cancelled');
                this.storage.setStorageItem('tenantTrialSub', subscription.status === 'in_trial');
            }),
            catchError(() => EMPTY),
            map(() => undefined)
        );
    }

    private openCompanies(redirectUrl: string | null): Observable<void> {
        return (this.storage.companyId ? of(0) : timer(1000)).pipe(
            switchMap(() => this.companyGroupService.getTree(false).pipe(
                filter(data => !!data),
                take(1)
            )),
            switchMap(data => this.companyGroupService.getCurrent().pipe(
                filter(company => this.companyReady(data, company)),
                take(1),
                map(company => ({data, company}))
            )),
            tap(() => this.gaService.trackG4Event('login_GA4')),
            switchMap(({company}) => this.authService.getTenantProfile().pipe(
                filter(profile => !!profile),
                take(1),
                tap(profile => this.applyProfile(profile)),
                switchMap(() => this.permissionsService.loadPermissions().pipe(take(1))),
                switchMap(() => this.routeAfterLogin(company, redirectUrl))
            ))
        );
    }

    private companyReady(data: {length: number} | null, company: unknown): boolean {
        return !!this.storage.selectedLevel && ((!!data?.length && !!company) || !data?.length);
    }

    private applyProfile(profile: any) {
        if (profile['ui_theme_color'] !== undefined && profile['ui_theme_color'] !== null) {
            this.storage.colorTheme = ColorThemeFromServerEnum[profile['ui_theme_color']];
            this.colorThemeService.setupColorTheme(ColorThemeFromServerEnum[profile['ui_theme_color']]);
        }
        this.storage.startingPage = profile.home_page || StartingPage.Insights;
    }

    private routeAfterLogin(company: any, redirectUrl: string | null): Observable<void> {
        if (redirectUrl) {
            // The auth guard passes the hash route without its '#' ('/settings/...'); older links
            // carry '#/settings/...'. Assigned bare, the former leaves the SPA for a server path.
            const target = decodeURIComponent(redirectUrl);
            window.location.href = target.startsWith('/') ? `#${target}` : target;
            return EMPTY;
        }
        if (this.storage.menuClicked) {
            return EMPTY;
        }
        if (company && progressRoles.indexOf(company.getRoleName().toLowerCase()) < 0) {
            this.routingService.go(this.defaultRouteService.getLoginRoute(
                this.storage, this.permissionsService, this.storage.selectedLevel?.type
            ));
            return EMPTY;
        }
        return this.firstStepService.getSteps().pipe(
            take(1),
            switchMap(steps => {
                if (!steps.section1.q1 && this.storage.model === 'tenant') {
                    this.routingService.go(STATE_NAMES.settingsCompanies);
                    return EMPTY;
                }
                const state = this.defaultRouteService.getLoginRoute(
                    this.storage, this.permissionsService, this.storage.selectedLevel?.type
                );
                const route = steps.progress < 100 && !steps.isClosed ? STATE_NAMES.progress : state;
                return timer(2000).pipe(tap(() => {
                    if (!this.storage.menuClicked) {
                        this.routingService.go(route);
                    }
                }));
            }),
            map(() => undefined)
        );
    }
}
